import { memo, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Clock, Eye, Rocket, Send, ShieldAlert, ShieldCheck, ShieldX } from 'lucide-react';
import { CopyOnClick } from '@/components/planilha/CopyOnClick';
import { usePainelPedido } from '@/contexts/PainelPedidoContext';
import { PrazoSlaBadge } from '@/components/kanban/PrazoSlaBadge';
import {
  CancelarItemTriagemButton,
  ContatoPedidoCell,
  TimelineCnpjButton,
  formatCnpjTriagem,
  resumoProdutos,
} from '@/components/kanban/triagem/triagemPedidoUtils';
import { temFibraParaInstalar, type PedidoTriagemFechado } from './useTriagemPedidoFechado';
import { AgendaInstalacaoPopover } from '@/components/kanban/instalacao/AgendaInstalacaoPopover';
import { TRIAGEM_SELO_BASE, TRIAGEM_SELO_PENDENTE, TRIAGEM_SELO_OK, TRIAGEM_SELO_REPROVADO, TRIAGEM_BTN, TRIAGEM_BTN_PRIMARIO } from '@/components/kanban/triagem/triagemTabela';
import { AcoesTriagem, BotaoManter } from '@/components/kanban/triagem/AcoesTriagem';

interface TriagemPedidoFechadoRowProps {
  pedido: PedidoTriagemFechado;
  posicao: number;
  isTopo: boolean;
  meuId: string | null;
  bloquearPorDono: boolean;
  isProcessing: boolean;
  onPegar: (pedido: PedidoTriagemFechado) => void;
  onAuditoriaCpf: (pedido: PedidoTriagemFechado) => void;
  onAuditoriaCpfComData: (pedido: PedidoTriagemFechado, dataIso: string) => void;
  onManterFimFila: (pedido: PedidoTriagemFechado) => void;
  onAfterCancel: () => void;
}

function ConferenciaBkoBadge({ status }: { status: PedidoTriagemFechado['bko_validacao'] }) {
  if (status === 'pendente') {
    return (
      <span className={`${TRIAGEM_SELO_BASE} ${TRIAGEM_SELO_PENDENTE}`} title="Conferência BKO PENDENTE — abra o painel do pedido e Aprove/Reprove antes de iniciar a criação">
        <ShieldAlert className="h-2.5 w-2.5" /> Pendente
      </span>
    );
  }
  if (status === 'validado') {
    return (
      <span className={`${TRIAGEM_SELO_BASE} ${TRIAGEM_SELO_OK}`} title="Conferência BKO validada">
        <ShieldCheck className="h-2.5 w-2.5" /> OK
      </span>
    );
  }
  if (status === 'reprovado') {
    return (
      <span className={`${TRIAGEM_SELO_BASE} ${TRIAGEM_SELO_REPROVADO}`} title="Conferência BKO reprovada">
        <ShieldX className="h-2.5 w-2.5" /> Reprov.
      </span>
    );
  }
  return <span className="text-[10px] text-muted-foreground">-</span>;
}

export const TriagemPedidoFechadoRow = memo(function TriagemPedidoFechadoRow({
  pedido,
  posicao,
  isTopo,
  meuId,
  bloquearPorDono,
  isProcessing,
  onPegar,
  onAuditoriaCpf,
  onAuditoriaCpfComData,
  onManterFimFila,
  onAfterCancel,
}: TriagemPedidoFechadoRowProps) {
  const { open: openPedido } = usePainelPedido();
  const [agendaAberta, setAgendaAberta] = useState(false);

  const ehCpf = pedido.tipo_pessoa === 'PF';
  const ehMeu = !!meuId && pedido.dono_id === meuId;
  const bloqueioDono = !ehCpf && bloquearPorDono && !ehMeu
    ? (pedido.dono_id ? `Pedido de ${pedido.dono_nome || 'outro BKO'}` : 'Aguardando distribuição')
    : null;
  const bloqueadoTitle = bloqueioDono || 'Fila FIFO: trabalhe primeiro o pedido do topo';
  const conferenciaPendente = pedido.bko_validacao === 'pendente';
  /* CPF: A FILA ORDENA, NÃO TRAVA (Douglas 21/10/2026). A auditoria por voz depende de o
     cliente atender: quem não atende segurava todos atrás, até os já auditados (caso Andrew,
     2610200012, 4º da fila em 20/10, virou ordem "tira daqui" à Conecta). Mesma régua da
     triagem eSIM (21/09). O CNPJ segue no FIFO. */
  const travaFila = !isTopo && !ehCpf;
  const acoesBloqueadas = travaFila || isProcessing || !!bloqueioDono;
  const cpfComFibra = ehCpf && temFibraParaInstalar(pedido);
  const { resumo, completo } = resumoProdutos(pedido.itens);
  const qtdTotal = pedido.itens.reduce((sum, i) => sum + (i.quantidade || 0), 0);

  /* §7 da régua: só o 1º da fila tem destaque — filete bordô POR DENTRO
     (inset 3px) + fundo quente. Os demais ficam neutros: `opacity` saiu porque
     de-ênfase é por COR, e opacity apagava junto o texto que precisa ser lido. */
  const rowClass = isTopo
    ? 'box-border h-[33px] border-b border-border/30 bg-accent/40 shadow-[inset_3px_0_0_hsl(var(--primary))] hover:bg-accent/70 transition-colors'
    : 'box-border h-[33px] border-b border-border/30 hover:bg-muted/30 transition-colors';

  return (
    <tr className={rowClass}>
      <td className="px-2 py-0 align-middle">
        <span className={`inline-flex items-center gap-1 text-[10px] font-bold tabular-nums ${isTopo ? 'text-primary' : 'text-muted-foreground'}`}>
          {isTopo ? '▶' : null}
          {posicao + 1}º
          {pedido.manter_em && <Clock className="h-3 w-3 text-primary" aria-label="Passado para o fim da fila" />}
        </span>
      </td>

      <td className="px-2 py-0 align-middle">
        <CopyOnClick value={pedido.documento || pedido.cnpj} className="tabular-nums text-xs">
          {formatCnpjTriagem(pedido.documento || pedido.cnpj)}
        </CopyOnClick>
      </td>

      <td className="px-2 py-0 align-middle">
        <span className="text-xs text-foreground truncate block max-w-[140px]" title={pedido.razao_social}>
          {pedido.razao_social || '-'}
        </span>
      </td>

      <td className="px-2 py-0 align-middle">
        <span className="text-xs text-muted-foreground truncate block max-w-[110px]" title={`${pedido.consultor_nome}${pedido.equipe_nome ? ` • ${pedido.equipe_nome}` : ''}`}>
          {pedido.consultor_nome || '-'}{pedido.equipe_nome ? ` • ${pedido.equipe_nome}` : ''}
        </span>
      </td>

      <td className="px-2 py-0 align-middle">
        {ehCpf ? (
          <span className="text-[10px] text-muted-foreground" title="CPF: fila comum, sem dono">fila comum</span>
        ) : ehMeu ? (
          <span className="text-[10px] font-bold text-primary">VOCÊ</span>
        ) : pedido.dono_id ? (
          <span className="text-xs text-muted-foreground truncate block max-w-[90px]" title={pedido.dono_nome}>{pedido.dono_nome || '-'}</span>
        ) : (
          <span className="text-[10px] italic text-muted-foreground" title="O CRM distribui entre os BKOs do Contrato que já agiram hoje">distribuindo</span>
        )}
      </td>

      <td className="px-2 py-0 align-middle">
        <button
          onClick={(e) => { e.stopPropagation(); openPedido(pedido.pedido_id, 'visao360'); }}
          className="text-xs font-medium text-primary hover:underline cursor-pointer"
          title="Abrir Painel do Pedido para análise e Conferência BKO"
        >
          {pedido.numero_pedido}
        </button>
      </td>

      <td className="px-2 py-0 align-middle">
        <span className="text-xs text-muted-foreground truncate block" title={completo}>
          {resumo || '-'}
        </span>
      </td>

      <td className="px-2 py-0 align-middle">
        <span className="text-xs text-muted-foreground">{qtdTotal || '-'}</span>
      </td>

      <td className="px-2 py-0 align-middle">
        <span className="text-xs text-muted-foreground whitespace-nowrap">
          {pedido.valor_total ? `R$ ${pedido.valor_total.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` : '-'}
        </span>
      </td>

      <td className="px-2 py-0 align-middle">
        <ConferenciaBkoBadge status={pedido.bko_validacao} />
      </td>

      <td className="px-2 py-0 align-middle">
        <PrazoSlaBadge pedidoId={pedido.pedido_id} coluna="PEDIDO FECHADO" entradaColunaEm={pedido.entrada_coluna_em} prazoHoras={20} />
      </td>

      <td className="px-2 py-0 align-middle">
        <div className="flex items-center gap-2">
          <ContatoPedidoCell telefone={pedido.telefone} leadOrigemId={pedido.lead_origem_id} />
          <TimelineCnpjButton cnpj={pedido.cnpj} />
        </div>
      </td>

      <td className="px-2 py-0 align-middle">
        <AcoesTriagem
          manter={
            <BotaoManter
              title={acoesBloqueadas && !isProcessing ? bloqueadoTitle : 'Manter — não dá para pegar agora? Justifique e passe para o fim da fila'}
              onClick={() => onManterFimFila(pedido)}
              disabled={acoesBloqueadas}
            />
          }
          avancar={
            cpfComFibra ? (
              <AgendaInstalacaoPopover
                titulo="Auditoria feita — enviar para Input"
                descricao="Pedido com fibra: marque a data e o turno da instalação."
                confirmar="Confirmar auditoria"
                salvando={isProcessing}
                aberto={agendaAberta}
                onAberto={(o) => setAgendaAberta(o && !acoesBloqueadas)}
                onSalvar={({ dataIso }) => { onAuditoriaCpfComData(pedido, dataIso); setAgendaAberta(false); }}
              >
                <Button
                  size="icon"
                  variant="ghost"
                  className={TRIAGEM_BTN_PRIMARIO}
                  title={acoesBloqueadas && !isProcessing ? bloqueadoTitle : 'Auditoria feita — marcar a data da instalação da fibra e enviar para Input'}
                  disabled={acoesBloqueadas}
                >
                  <Send className="h-3.5 w-3.5" />
                </Button>
              </AgendaInstalacaoPopover>
            ) : ehCpf ? (
              <Button
                size="icon"
                variant="ghost"
                className={TRIAGEM_BTN_PRIMARIO}
                title={acoesBloqueadas && !isProcessing ? bloqueadoTitle : 'Auditoria feita — enviar para Input (CPF não tem biometria nem aceite)'}
                onClick={() => onAuditoriaCpf(pedido)}
                disabled={acoesBloqueadas}
              >
                <Send className="h-3.5 w-3.5" />
              </Button>
            ) : (
            <Button
              size="icon"
              variant="ghost"
              className={TRIAGEM_BTN_PRIMARIO}
              title={
                acoesBloqueadas && !isProcessing
                  ? bloqueadoTitle
                  : conferenciaPendente
                    ? 'Conferência BKO pendente — clique para abrir o painel e Aprovar/Reprovar'
                    : 'Pegar e criar — o pedido vai para a SUA fila de criação, com o seu nome'
              }
              onClick={() => {
                if (conferenciaPendente) {
                  toast.info('Conferência BKO pendente — Aprove ou Reprove no painel do pedido para liberar a criação.', { duration: 6000 });
                  openPedido(pedido.pedido_id, 'visao360');
                  return;
                }
                onPegar(pedido);
              }}
              disabled={acoesBloqueadas}
            >
              <Rocket className="h-3.5 w-3.5" />
            </Button>
            )
          }
          ferramentas={
            <Button
              size="icon"
              variant="ghost"
              className={TRIAGEM_BTN}
              title="Analisar — Painel do Pedido (dados, aprovação e Conferência BKO) (Pedido 360)"
              onClick={() => openPedido(pedido.pedido_id, 'visao360')}
            >
              <Eye className="h-3 w-3" />
            </Button>
          }
          cancelar={
            <CancelarItemTriagemButton
              pedidoId={pedido.pedido_id}
              itens={pedido.itens}
              disabled={acoesBloqueadas}
              disabledTitle={bloqueadoTitle}
              onAfterCancel={onAfterCancel}
            />
          }
        />
      </td>
    </tr>
  );
});
